'use client'

import { PublicAxiosInstance } from '@/api/axios'
import { Button } from '@/components/ui/button'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp'
import { Label } from '@/components/ui/label'
import type { ISignatureOtpChallenge } from '@/interface/signature.interface'
import { cn } from '@/lib/utils'
import type { AxiosError } from 'axios'
import { Check, Clock, Mail } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

export interface SignatureOtpProps {
  /** Secure signing token from the URL. */
  token: string
  /** Fires once the code is verified, with the challenge to submit alongside the signature. */
  onVerified: (challengeId: string) => void
  /** Fires when the code expires, verified or not; the challenge can no longer be submitted. */
  onExpired?: () => void
  disabled?: boolean
  className?: string
}

const errorMessage = (error: unknown, fallback: string) =>
  (error as AxiosError<{ message: string }>)?.response?.data?.message || fallback

const COUNTDOWN_TICK_MS = 1000

const secondsUntil = (isoDate: string) => Math.max(0, Math.ceil((new Date(isoDate).getTime() - Date.now()) / 1000))

const formatCountdown = (totalSeconds: number) => {
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

/**
 * Email one-time code step-up.
 *
 * Possession of a forwarded signing link is weak evidence of identity; a code
 * sent to the address on the signer's staff record raises it to control of that
 * mailbox at signing time.
 */
const SignatureOtp = ({ token, onVerified, onExpired, disabled = false, className }: SignatureOtpProps) => {
  const [challenge, setChallenge] = useState<ISignatureOtpChallenge | null>(null)
  const [code, setCode] = useState('')
  const [sending, setSending] = useState(false)
  const [verifying, setVerifying] = useState(false)
  const [verified, setVerified] = useState(false)
  const [secondsLeft, setSecondsLeft] = useState(0)
  const [expired, setExpired] = useState(false)

  // Count down to the code's server-side expiry, before and after verification.
  // Once it lapses the challenge is dropped and the signer must verify again.
  useEffect(() => {
    if (!challenge) return

    const tick = () => {
      const remaining = secondsUntil(challenge.expiresAt)
      setSecondsLeft(remaining)
      if (remaining > 0) return
      setChallenge(null)
      setCode('')
      setVerified(false)
      setExpired(true)
      onExpired?.()
    }

    tick()
    const interval = window.setInterval(tick, COUNTDOWN_TICK_MS)
    return () => window.clearInterval(interval)
  }, [challenge, onExpired])

  const requestCode = async () => {
    try {
      setSending(true)
      const response = await PublicAxiosInstance.post(`/policy/review-and-approval/${token}/otp`, {})
      setChallenge(response.data.data as ISignatureOtpChallenge)
      setCode('')
      setVerified(false)
      setExpired(false)
      toast.success('Verification code sent')
    } catch (error) {
      toast.error(errorMessage(error, 'Could not send the verification code'))
    } finally {
      setSending(false)
    }
  }

  const verifyCode = async (value: string) => {
    if (!challenge) return

    try {
      setVerifying(true)
      await PublicAxiosInstance.post(`/policy/review-and-approval/${token}/otp/verify`, {
        challengeId: challenge.challengeId,
        code: value,
      })
      setVerified(true)
      onVerified(challenge.challengeId)
      toast.success('Identity verified')
    } catch (error) {
      setCode('')
      toast.error(errorMessage(error, 'That code was not accepted'))
    } finally {
      setVerifying(false)
    }
  }

  const countdown = (
    <p
      className={cn('flex items-center gap-1.5 text-xs text-muted-foreground', secondsLeft <= 60 && 'text-destructive')}
    >
      <Clock className='h-3.5 w-3.5' />
      {verified ? 'Verification expires in' : 'Code expires in'}{' '}
      <span className='font-mono tabular-nums'>{formatCountdown(secondsLeft)}</span>
    </p>
  )

  if (verified)
    return (
      <div
        className={cn(
          'flex flex-wrap items-center justify-between gap-2 rounded-md border border-green-600/30 bg-green-50 p-3 dark:bg-green-950/30',
          className
        )}
      >
        <div className='flex items-center gap-2'>
          <Check className='h-4 w-4 text-green-700 dark:text-green-400' />
          <span className='text-sm text-green-800 dark:text-green-300'>
            Identity verified. You can now complete your signature.
          </span>
        </div>
        {countdown}
      </div>
    )

  return (
    <div className={cn('space-y-3 rounded-md border p-4', className)}>
      <div className='flex items-center gap-2'>
        <Mail className='h-4 w-4 text-muted-foreground' />
        <h3 className='text-sm font-semibold'>
          Verify your identity<span className='ml-1 text-destructive'>*</span>
        </h3>
      </div>

      {!challenge ? (
        <>
          {expired ? (
            <p role='alert' className='text-sm text-destructive'>
              Your verification code has expired. Request a new code to verify your identity again.
            </p>
          ) : (
            <p className='text-sm text-muted-foreground'>
              Your organisation requires an email verification code before this policy can be signed.
            </p>
          )}
          <Button type='button' size='sm' onClick={requestCode} disabled={disabled || sending}>
            {sending ? 'Sending…' : expired ? 'Send a new code' : 'Send verification code'}
          </Button>
        </>
      ) : (
        <>
          <p className='text-sm text-muted-foreground'>
            Enter the 6 digit code sent to <span className='font-medium'>{challenge.maskedEmail}</span>.
          </p>

          <div className='space-y-1.5'>
            <Label htmlFor='signature-otp'>Verification code</Label>
            <InputOTP
              id='signature-otp'
              maxLength={6}
              value={code}
              disabled={disabled || verifying}
              onChange={(value) => {
                setCode(value)
                // Verify as soon as the code is complete; making the signer press
                // a second button here adds friction for no benefit.
                if (value.length === 6) void verifyCode(value)
              }}
            >
              <InputOTPGroup>
                {[0, 1, 2, 3, 4, 5].map((index) => (
                  <InputOTPSlot key={index} index={index} />
                ))}
              </InputOTPGroup>
            </InputOTP>
          </div>

          <div className='flex flex-wrap items-center justify-between gap-2'>
            {countdown}
            <Button type='button' variant='outline' size='sm' onClick={requestCode} disabled={disabled || sending}>
              {sending ? 'Sending…' : 'Resend code'}
            </Button>
          </div>
        </>
      )}
    </div>
  )
}

export default SignatureOtp
